import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";

const fmt = (n) => "$" + (parseFloat(n) || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ov = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  modal: { background: "#fff", borderRadius: 14, padding: 26, width: "100%", maxWidth: 460, boxShadow: "0 10px 40px rgba(0,0,0,0.2)" },
  title: { margin: 0, fontSize: 18, fontWeight: 700, color: "#222" },
  sub: { margin: "4px 0 18px", fontSize: 13, color: "#888" },
  label: { display: "block", fontSize: 12, fontWeight: 700, color: "#666", marginBottom: 5, marginTop: 14, textTransform: "uppercase", letterSpacing: "0.04em" },
  input: { width: "100%", padding: "9px 12px", border: "1.5px solid #e0d6dc", borderRadius: 8, fontSize: 14, outline: "none", color: "#222", boxSizing: "border-box" },
  calc: { marginTop: 8, fontSize: 13, color: "#9f0059", fontWeight: 600 },
  btnRow: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 },
  btnCancel: { padding: "9px 18px", background: "#fff", color: "#888", border: "1.5px solid #e0d6dc", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" },
  btnSave: { padding: "9px 20px", background: "#9f0059", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" },
};

// comision: objeto para editar (null = alta). userId: usuario logueado. onSaved/onClose.
const ComisionModal = ({ comision, userId, onClose, onSaved }) => {
  const esEdicion = !!comision;

  const [usuarios,   setUsuarios]   = useState([]);
  const [ordenes,    setOrdenes]    = useState([]); // OCs entregadas (solo en alta)
  const [ordenId,    setOrdenId]    = useState(comision?.orden_id ? String(comision.orden_id) : "");
  const [montoOc,    setMontoOc]    = useState(comision?.monto_oc ? parseFloat(comision.monto_oc) : 0);
  const [clienteOc,  setClienteOc]  = useState(comision?.cliente || "");
  const [usuarioId,  setUsuarioId]  = useState(comision?.usuario_id ? String(comision.usuario_id) : "");
  const [porcentaje, setPorcentaje] = useState(comision?.porcentaje ? String(comision.porcentaje) : "");
  const [notas,      setNotas]      = useState(comision?.notas || "");
  const [saving,     setSaving]     = useState(false);

  // Cargar usuarios internos (tipo administrador)
  useEffect(() => {
    axios.post(`${BASE}/buscar_usuarios.php`)
      .then((r) => {
        const comerciales = (Array.isArray(r.data) ? r.data : []).filter(
          (u) => Number(u.comercial) === 1 && Number(u.activo) === 1
        );
        setUsuarios(comerciales);
      })
      .catch(() => {});
  }, []);

  // En alta: cargar OCs entregadas para elegir
  useEffect(() => {
    if (esEdicion) return;
    axios.post(`${BASE}/buscar_entregadas.php`, new URLSearchParams({ meses: 999 }))
      .then((r) => setOrdenes(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [esEdicion]);

  const onPickOrden = (val) => {
    setOrdenId(val);
    const o = ordenes.find((x) => String(x.id) === val);
    setMontoOc(o ? parseFloat(o.monto_total) || 0 : 0);
    setClienteOc(o ? o.cliente : "");
  };

  const pct = parseFloat(porcentaje) || 0;
  const montoComision = montoOc * pct / 100;

  const guardar = async () => {
    if (!esEdicion && !ordenId) { toast.error("Elegí una orden entregada."); return; }
    if (!usuarioId) { toast.error("Elegí el comisionado."); return; }
    if (pct <= 0)   { toast.error("El porcentaje debe ser mayor a 0."); return; }
    setSaving(true);
    try {
      const fd = new URLSearchParams({
        usuario_id: usuarioId,
        porcentaje: pct,
        notas,
        cargada_por_usuario_id: userId ?? "",
      });
      if (esEdicion) fd.append("id", comision.id);
      else           fd.append("orden_id", ordenId);

      const r = await axios.post(`${BASE}/comisiones_guardar.php`, fd);
      if (r.data?.success) {
        toast.success(esEdicion ? "Comisión actualizada" : "Comisión cargada");
        onSaved && onSaved();
      } else {
        toast.error(r.data?.mensaje || "Error al guardar");
      }
    } catch { toast.error("Error de conexión"); }
    finally  { setSaving(false); }
  };

  return (
    <div style={ov.overlay} onClick={onClose}>
      <div style={ov.modal} onClick={(e) => e.stopPropagation()}>
        <p style={ov.title}>{esEdicion ? "Editar comisión" : "Nueva comisión"}</p>
        <p style={ov.sub}>Comisión sobre el monto total de la OC entregada.</p>

        {/* OC */}
        {esEdicion ? (
          <div style={{ background: "#faf5f8", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#444" }}>
            <strong>OC #{comision.orden_id}</strong>{clienteOc ? ` · ${clienteOc}` : ""} · {fmt(montoOc)}
          </div>
        ) : (
          <>
            <label style={ov.label}>Orden entregada *</label>
            <select style={ov.input} value={ordenId} onChange={(e) => onPickOrden(e.target.value)}>
              <option value="">— Elegí una OC —</option>
              {ordenes.map((o) => (
                <option key={o.id} value={o.id}>
                  OC #{o.id} · {o.cliente || "—"} · {fmt(o.monto_total)}
                </option>
              ))}
            </select>
          </>
        )}

        {/* Usuario */}
        <label style={ov.label}>Comisionado *</label>
        <select style={ov.input} value={usuarioId} onChange={(e) => setUsuarioId(e.target.value)}>
          <option value="">— Elegí un usuario —</option>
          {usuarios.map((u) => (
            <option key={u.id} value={u.id}>{u.nombre}</option>
          ))}
        </select>

        {/* Porcentaje */}
        <label style={ov.label}>Porcentaje (%) *</label>
        <input
          style={ov.input}
          type="number"
          min="0"
          step="0.01"
          placeholder="ej: 3.5"
          value={porcentaje}
          onChange={(e) => setPorcentaje(e.target.value)}
        />
        {montoOc > 0 && pct > 0 && (
          <div style={ov.calc}>Sobre {fmt(montoOc)} (monto OC) = {fmt(montoComision)}</div>
        )}

        {/* Notas */}
        <label style={ov.label}>Notas (opcional)</label>
        <textarea
          style={{ ...ov.input, minHeight: 60, resize: "vertical", fontFamily: "inherit" }}
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
        />

        <div style={ov.btnRow}>
          <button style={ov.btnCancel} onClick={onClose} disabled={saving}>Cancelar</button>
          <button style={{ ...ov.btnSave, opacity: saving ? 0.6 : 1 }} onClick={guardar} disabled={saving}>
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComisionModal;
